import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";

const OurExpectise = ({ data }) => {
  const happyClientsPage = data.page.find(
    (p) => p.title.toLowerCase() === "happy clients"
  );

  const stories = happyClientsPage.sections[0].list;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  const totalStories = stories.length;
  const currentStory = stories[currentIndex];

  const carouselStats = useMemo(
    () => ({
      progress: ((currentIndex + 1) / totalStories) * 100,
      displayIndex: String(currentIndex + 1).padStart(2, "0"),
    }),
    [currentIndex, totalStories]
  );

  const goTo = (index) => {
    setCurrentIndex((index + totalStories) % totalStories);
  };

  const handlePrev = () => goTo(currentIndex - 1);
  const handleNext = () => goTo(currentIndex + 1);

  useEffect(() => {
    if (!isAutoPlay) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalStories);
    }, 4500);
    return () => clearInterval(interval);
  }, [isAutoPlay, totalStories]);

  return (
    <section className="relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-gradient-to-b from-[#f7f1eb] via-white to-[#eef1f7]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(28,61,114,0.08),transparent_55%)]" />
      <div className="absolute inset-x-0 -top-24 h-52 bg-white/70 blur-[120px]" />

      <div className="relative z-10 max-w-6xl mx-auto space-y-10">
        <div className="text-center space-y-4">
          <p className="text-[11px] sm:text-sm uppercase tracking-[0.35em] text-black/45">
            {happyClientsPage.sections[0].heading}
          </p>
          <h2 className="text-2xl sm:text-4xl font-semibold text-black/85">
            The women behind the numbers
          </h2>
          <p className="text-sm sm:text-lg text-black/60 max-w-3xl mx-auto">
            Personal stories of grit, community leadership and financial
            confidence nurtured by microcredit and the support of the BSS Sonata
            field teams.
          </p>
        </div>

        <div className="grid lg:grid-cols-[420px_minmax(0,1fr)] gap-8 items-stretch">
          <article className="group relative overflow-hidden rounded-[32px] mob-h border border-black/5 bg-gradient-to-b from-transparent to-black text-white shadow-[0_30px_80px_rgba(13,20,40,0.15)]">
            <img
              src={currentStory.image}
              alt={currentStory.name}
              className="absolute  inset-0 h-full w-full object-cover object-top transition duration-500 group-hover:scale-105 "
            />
            <div className="relative flex h-full flex-col justify-between p-6 sm:p-8 lg:p-10">
              <div className="space-y-3">
                <h3 className="text-2xl sm:text-3xl text-black font-semibold drop-shadow">
                  {currentStory.name}
                </h3>
              </div>
              <div className="flex flex-col gap-4">
                <div className="text-3xl sm:text-4xl font-light">
                  {carouselStats.displayIndex}
                  <span className="text-lg text-white/60">
                    {" "}
                    / {String(totalStories).padStart(2, "0")}
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-black/55">
                  <div
                    className="h-full rounded-full bg-white transition-all duration-500 ease-out"
                    style={{ width: `${carouselStats.progress}%` }}
                  />
                </div>
                <div className="flex justify-between gap-2">
                  <button
                    onClick={handlePrev}
                    className="flex-1 rounded-2xl border border-white/30 bg-black/80 py-3 text-xs font-semibold uppercase tracking-[0.35em] text-white hover:bg-white/25 transition"
                  >
                    Prev
                  </button>
                  <button
                    onClick={handleNext}
                    className="flex-1 rounded-2xl border border-white/30 bg-white py-3 text-xs font-semibold uppercase tracking-[0.35em] text-black hover:bg-white/90 transition"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setIsAutoPlay((prev) => !prev)}
                    className="w-12 rounded-2xl border border-white/30 bg-black/85 text-white hover:bg-white/25 transition flex items-center justify-center"
                    aria-label={isAutoPlay ? "Pause slider" : "Play slider"}
                  >
                    {isAutoPlay ? <Pause size={18} /> : <Play size={18} />}
                  </button>
                </div>
              </div>
            </div>
          </article>

          <div className="flex flex-col gap-8">
            <div className="rounded-[32px] border border-black/5 bg-white shadow-[0_35px_90px_rgba(15,23,42,0.12)] p-6 sm:p-8 space-y-6">
              <p className="text-[11px] uppercase tracking-[0.4em] text-black/45">
                testimonial
              </p>
              <p className="text-base sm:text-xl text-black/80 leading-relaxed">
                “{currentStory.massage}”
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-red/90 text-white flex items-center justify-center text-sm uppercase tracking-[0.2em]">
                  {currentStory.name.slice(0, 2)}
                </div>
                <div>
                  <p className="text-sm sm:text-base font-semibold text-black">
                    {currentStory.name}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.4em] text-black/40">
                    Client partner
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-[32px] border border-black/5 bg-white p-4 sm:p-6 text-black">
              <p className="text-[11px] uppercase tracking-[0.45em] text-black/40 mb-4">
                more journeys
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {stories.map((story, index) => {
                  const isActive = index === currentIndex;
                  return (
                    <button
                      key={story.id}
                      onClick={() => goTo(index)}
                      className={`group rounded-2xl border transition-all duration-300 text-left overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                        isActive
                          ? "border-black/20 shadow-lg"
                          : "border-black/5 hover:border-black/20"
                      }`}
                    >
                      <img
                        src={story.image}
                        alt={story.name}
                        className={`h-28 w-full rounded-t-2xl object-cover object-top transition duration-300 ${
                          isActive ? "scale-105" : "group-hover:scale-105"
                        }`}
                      />
                      <div className="p-3 space-y-1 rounded-b-2xl bg-white">
                        <p className="text-[10px] uppercase tracking-[0.35em] text-black/50">
                          Story {String(index + 1).padStart(2, "0")}
                        </p>
                        <p className="text-xs sm:text-sm font-semibold text-black">
                          {story.name}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurExpectise;
